import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { organizationAPI } from '../../services/api';
import { getFileUrl } from '../../utils/fileUrl';
import { saveAs } from 'file-saver';
import SuccessModal from '../Common/SuccessModal';
import ConfirmModal from '../Common/ConfirmModal';
import ErrorModal from '../Common/ErrorModal';
import {
  AdminPage,
  PageToolbar,
  ExportMenu,
  CardGrid,
  EntityCard,
  Pagination,
} from '../Common/admin';

const PAGE_SIZE = 12;

const OrganizationList = () => {
  const { t } = useTranslation();
  const [organizations, setOrganizations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [appliedSearch, setAppliedSearch] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [selectedIds, setSelectedIds] = useState([]);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null);
  const [confirmData, setConfirmData] = useState(null);

  const loadOrganizations = useCallback(async () => {
    try {
      setLoading(true);
      const response = await organizationAPI.getAll(appliedSearch, page, PAGE_SIZE);
      setOrganizations(response.data.items || []);
      setTotalPages(response.data.totalPages || 1);
      setTotalCount(response.data.totalCount ?? 0);
      setSelectedIds([]);
    } catch (err) {
      setErrorMessage(t('organization.list.messages.loadError'));
      setShowErrorModal(true);
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [appliedSearch, page, t]);

  useEffect(() => {
    loadOrganizations();
  }, [loadOrganizations]);

  const handleSearch = () => {
    setPage(1);
    setAppliedSearch(searchTerm);
  };

  const handleDelete = (id) => {
    setConfirmAction(() => async () => {
      try {
        await organizationAPI.delete(id);
        setSuccessMessage(t('organization.list.messages.deleteSuccess'));
        setShowSuccessModal(true);
        loadOrganizations();
      } catch (err) {
        setErrorMessage(t('organization.list.messages.deleteError'));
        setShowErrorModal(true);
      }
    });
    setConfirmData({
      title: t('common.confirm'),
      message: t('organization.list.messages.deleteConfirm'),
      confirmText: t('common.delete'),
      cancelText: t('common.cancel'),
    });
    setShowConfirmModal(true);
  };

  const handleBulkDelete = () => {
    if (selectedIds.length === 0) {
      setErrorMessage(t('organization.list.messages.selectToDelete'));
      setShowErrorModal(true);
      return;
    }

    setConfirmAction(() => async () => {
      try {
        const response = await organizationAPI.bulkDelete(selectedIds);
        setSuccessMessage(
          response.data.message ||
            t('organization.list.messages.bulkDeleteSuccess', { count: selectedIds.length })
        );
        setShowSuccessModal(true);
        setSelectedIds([]);
        loadOrganizations();
      } catch (err) {
        setErrorMessage(
          err.response?.data?.message || t('organization.list.messages.bulkDeleteError')
        );
        setShowErrorModal(true);
      }
    });
    setConfirmData({
      title: t('common.confirm'),
      message: t('organization.list.messages.bulkDeleteConfirm', { count: selectedIds.length }),
      confirmText: t('common.delete'),
      cancelText: t('common.cancel'),
    });
    setShowConfirmModal(true);
  };

  const handleConfirm = async () => {
    setShowConfirmModal(false);
    if (confirmAction) await confirmAction();
    setConfirmAction(null);
    setConfirmData(null);
  };

  const handleCancel = () => {
    setShowConfirmModal(false);
    setConfirmAction(null);
    setConfirmData(null);
  };

  const handleSelectAll = (e) => {
    setSelectedIds(e.target.checked ? organizations.map((o) => o.id) : []);
  };

  const handleSelectOne = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]
    );
  };

  const handleExport = async (format) => {
    try {
      let response;
      let filename;
      let contentType;
      const stamp = new Date().toISOString().slice(0, 10);

      switch (format) {
        case 'excel':
          response = await organizationAPI.exportExcel();
          filename = `Organizations_${stamp}.xlsx`;
          contentType = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
          break;
        case 'csv':
          response = await organizationAPI.exportCsv();
          filename = `Organizations_${stamp}.csv`;
          contentType = 'text/csv;charset=utf-8';
          break;
        case 'pdf':
          response = await organizationAPI.exportPdf();
          filename = `Organizations_${stamp}.pdf`;
          contentType = 'application/pdf';
          break;
        default:
          return;
      }

      saveAs(new Blob([response.data], { type: contentType }), filename);
      setSuccessMessage(
        t('organization.list.messages.exportSuccess', { format: format.toUpperCase() })
      );
      setShowSuccessModal(true);
    } catch (err) {
      console.error('Export error:', err);
      setErrorMessage(
        t('organization.list.messages.exportError', { format: format.toUpperCase() })
      );
      setShowErrorModal(true);
    }
  };

  const toolbarActions = (
    <>
      <ExportMenu onExport={handleExport} />
      <button
        type="button"
        className="ad-icon-btn ad-icon-btn-danger"
        title={t('common.bulkDelete')}
        onClick={handleBulkDelete}
        disabled={selectedIds.length === 0}
      >
        <i className="fas fa-trash"></i>
      </button>
    </>
  );

  return (
    <AdminPage title={t('organization.list.title')}>
      <PageToolbar
        addTo="/organization/create"
        addLabel={t('organization.list.addNew')}
        searchValue={searchTerm}
        searchPlaceholder={t('organization.list.searchPlaceholder')}
        onSearchChange={setSearchTerm}
        onSearchSubmit={handleSearch}
        selectAll={{
          checked: selectedIds.length === organizations.length && organizations.length > 0,
          onChange: handleSelectAll,
          count: selectedIds.length,
        }}
        actions={toolbarActions}
      />

      <CardGrid
        loading={loading}
        isEmpty={organizations.length === 0}
        emptyTitle={t('organization.list.messages.noData')}
        emptyText={t('common.noResultsHint')}
        skeletonCount={PAGE_SIZE}
      >
        {organizations.map((organization) => (
          <EntityCard
            key={organization.id}
            title={organization.organizationNameAr}
            subtitle={organization.organizationNameEng}
            code={organization.organizationCode}
            avatar={getFileUrl(organization.logo)}
            selected={selectedIds.includes(organization.id)}
            onSelect={() => handleSelectOne(organization.id)}
            fields={[
              { label: t('organization.list.columns.crNumber'), value: organization.crNumber },
              { label: t('organization.list.columns.vatNumber'), value: organization.vatNumber },
              {
                label: t('organization.list.columns.country'),
                value: organization.country?.nameEng,
              },
              {
                label: t('organization.list.columns.city'),
                value: organization.city?.cityNameEng,
              },
              { label: t('organization.list.columns.email'), value: organization.email },
            ]}
            badges={[
              {
                label: organization.isActive ? t('common.active') : t('common.inactive'),
                tone: organization.isActive ? 'success' : 'danger',
              },
            ]}
            viewTo={`/organization/view/${organization.id}`}
            editTo={`/organization/edit/${organization.id}`}
            onDelete={() => handleDelete(organization.id)}
          />
        ))}
      </CardGrid>

      {!loading && organizations.length > 0 && (
        <Pagination
          page={page}
          totalPages={totalPages}
          totalCount={totalCount}
          pageSize={PAGE_SIZE}
          onChange={setPage}
        />
      )}

      <ErrorModal
        show={showErrorModal}
        message={errorMessage}
        onClose={() => setShowErrorModal(false)}
      />
      <SuccessModal
        show={showSuccessModal}
        message={successMessage}
        onClose={() => setShowSuccessModal(false)}
      />
      <ConfirmModal
        show={showConfirmModal}
        title={confirmData?.title}
        message={confirmData?.message}
        confirmText={confirmData?.confirmText}
        cancelText={confirmData?.cancelText}
        onConfirm={handleConfirm}
        onCancel={handleCancel}
      />
    </AdminPage>
  );
};

export default OrganizationList;
